import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ExternalLink, Sparkles } from "lucide-react";
import {
  useGoal,
  useUpdateGoal,
  useDeleteGoal,
  usePlans,
  useActivities,
  useCreatePlan,
  useCreateActivity,
} from "@/hooks/useCareer";
import { Modal } from "@/components/ui/modal";
import {
  EntityRow,
  ProgressBar,
  StatusChip,
  fieldClassName,
} from "@/components/goals/goals-ui";
import {
  isOpenWork,
  suggestedProgressFromWork,
  formatConfidence,
} from "@/lib/goals-progress";

export function GoalDetailPage() {
  const { goalId } = useParams<{ goalId: string }>();
  const navigate = useNavigate();
  const goalQuery = useGoal(goalId);
  const updateGoal = useUpdateGoal();
  const deleteGoal = useDeleteGoal();
  const plansQuery = usePlans(goalId);
  const activitiesQuery = useActivities(false);
  const createPlan = useCreatePlan();
  const createActivity = useCreateActivity();

  const goal = goalQuery.data?.goal;
  const plans = plansQuery.data?.plans ?? [];
  const allWork = activitiesQuery.data?.activities ?? [];
  const goalWork = useMemo(
    () => allWork.filter((a) => a.goalId === goalId),
    [allWork, goalId],
  );
  const directWork = goalWork.filter((a) => !a.planId);
  const suggested = suggestedProgressFromWork(goalWork);

  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [success, setSuccess] = useState("");
  const [goalType, setGoalType] = useState("role");
  const [status, setStatus] = useState("active");
  const [targetDate, setTargetDate] = useState("");
  const [progressPercent, setProgressPercent] = useState(0);
  const [externalUrl, setExternalUrl] = useState("");
  const [confidence, setConfidence] = useState("");

  const [planOpen, setPlanOpen] = useState(false);
  const [planTitle, setPlanTitle] = useState("");
  const [planObjective, setPlanObjective] = useState("");
  const [workOpen, setWorkOpen] = useState(false);
  const [workTitle, setWorkTitle] = useState("");

  useEffect(() => {
    if (!goal) return;
    setTitle(goal.title);
    setSuccess(goal.successDefinition);
    setGoalType(goal.goalType);
    setStatus(goal.status);
    setTargetDate(goal.targetDate?.slice(0, 10) ?? "");
    setProgressPercent(goal.progressPercent ?? 0);
    setExternalUrl(goal.externalUrl ?? "");
    setConfidence(goal.confidence ?? "");
  }, [goal]);

  if (goalQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
      </div>
    );
  }

  if (!goal) {
    return (
      <div>
        <Link to="/goals" className="text-sm text-brand hover:underline">
          Back to Goals
        </Link>
        <p className="mt-4 text-sm text-destructive">Goal not found.</p>
      </div>
    );
  }

  async function save() {
    if (!goalId) return;
    await updateGoal.mutateAsync({
      id: goalId,
      input: {
        title: title.trim(),
        successDefinition: success.trim(),
        goalType,
        status,
        targetDate: targetDate || null,
        progressPercent,
        externalUrl: externalUrl.trim() || null,
        confidence: confidence || null,
      },
    });
    setEditing(false);
  }

  async function adoptSuggested() {
    if (!goalId || suggested == null) return;
    setProgressPercent(suggested);
    await updateGoal.mutateAsync({
      id: goalId,
      input: { progressPercent: suggested },
    });
  }

  async function handleDelete() {
    if (!goalId || !confirm("Delete this goal?")) return;
    await deleteGoal.mutateAsync(goalId);
    navigate("/goals");
  }

  return (
    <div className="mx-auto max-w-3xl pb-10">
      <Link to="/goals" className="mb-4 inline-flex text-sm text-brand hover:underline">
        Back to Goals
      </Link>

      <header className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-foreground">{goal.title}</h2>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <StatusChip>{goal.goalType}</StatusChip>
              <span>{goal.status}</span>
              {goal.targetDate ? (
                <span>target {goal.targetDate.slice(0, 10)}</span>
              ) : null}
              {formatConfidence(goal.confidence) ? (
                <span>{formatConfidence(goal.confidence)} confidence</span>
              ) : null}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to={`/ai-assistant?goalId=${encodeURIComponent(goal.id)}`}
              className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs text-muted-foreground hover:text-foreground"
            >
              <Sparkles className="size-3.5" />
              Ask AI
            </Link>
            <button
              type="button"
              onClick={() => setEditing((v) => !v)}
              className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium"
            >
              {editing ? "Cancel" : "Edit"}
            </button>
          </div>
        </div>
      </header>

      {editing ? (
        <section className="mb-8 space-y-3 rounded-lg border border-border bg-card p-4">
          <input
            className={fieldClassName()}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <textarea
            className={fieldClassName()}
            rows={3}
            value={success}
            onChange={(e) => setSuccess(e.target.value)}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <select
              className={fieldClassName()}
              value={goalType}
              onChange={(e) => setGoalType(e.target.value)}
            >
              <option value="role">Role</option>
              <option value="skill">Skill</option>
              <option value="network">Network</option>
              <option value="offer">Offer</option>
              <option value="custom">Custom</option>
            </select>
            <select
              className={fieldClassName()}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="completed">Completed</option>
              <option value="abandoned">Abandoned</option>
            </select>
            <input
              type="date"
              className={fieldClassName()}
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
            />
            <select
              className={fieldClassName()}
              value={confidence}
              onChange={(e) => setConfidence(e.target.value)}
            >
              <option value="">Confidence</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          <input
            className={fieldClassName()}
            placeholder="External link (e.g. Jira epic)"
            value={externalUrl}
            onChange={(e) => setExternalUrl(e.target.value)}
          />
          <label className="block text-xs text-muted-foreground">
            Your progress: {progressPercent}%
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              className="mt-1 w-full"
              value={progressPercent}
              onChange={(e) => setProgressPercent(Number(e.target.value))}
            />
          </label>
          <div className="flex justify-between gap-2">
            <button
              type="button"
              onClick={() => void handleDelete()}
              className="text-xs text-destructive hover:underline"
            >
              Delete goal
            </button>
            <button
              type="button"
              onClick={() => void save()}
              disabled={updateGoal.isPending}
              className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </section>
      ) : (
        <>
          <section className="mb-8">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Success definition
            </h3>
            <p className="mt-2 text-sm text-foreground">{goal.successDefinition}</p>
            {goal.externalUrl ? (
              <a
                href={goal.externalUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-sm text-brand hover:underline"
              >
                External link <ExternalLink className="size-3.5" />
              </a>
            ) : null}
          </section>

          <section className="mb-8">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Progress
            </h3>
            <div className="mt-2 flex items-center gap-3">
              <ProgressBar value={goal.progressPercent ?? 0} className="flex-1" />
              <span className="text-sm tabular-nums text-foreground">
                {goal.progressPercent ?? 0}%
              </span>
            </div>
            {suggested != null ? (
              <p className="mt-2 text-xs text-muted-foreground">
                Work suggests ~{suggested}%{" "}
                <button
                  type="button"
                  className="text-brand hover:underline"
                  onClick={() => void adoptSuggested()}
                >
                  Adopt suggested
                </button>
              </p>
            ) : null}
            <label className="mt-3 block text-xs text-muted-foreground">
              Update progress
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                className="mt-1 w-full"
                value={progressPercent}
                onChange={(e) => setProgressPercent(Number(e.target.value))}
                onMouseUp={() =>
                  void updateGoal.mutateAsync({
                    id: goal.id,
                    input: { progressPercent },
                  })
                }
                onTouchEnd={() =>
                  void updateGoal.mutateAsync({
                    id: goal.id,
                    input: { progressPercent },
                  })
                }
              />
            </label>
          </section>
        </>
      )}

      <section className="mb-8">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Plans
          </h3>
          <button
            type="button"
            onClick={() => setPlanOpen(true)}
            className="text-xs text-brand hover:underline"
          >
            Add plan
          </button>
        </div>
        {plans.length === 0 ? (
          <p className="text-sm text-muted-foreground">No plans linked yet.</p>
        ) : (
          <ul className="space-y-2">
            {plans.map((p) => (
              <EntityRow
                key={p.id}
                to={`/plans/${p.id}`}
                title={p.title}
                meta={
                  <span>
                    {p.kind} · {p.status}
                  </span>
                }
              />
            ))}
          </ul>
        )}
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Direct work
          </h3>
          <button
            type="button"
            onClick={() => setWorkOpen(true)}
            className="text-xs text-brand hover:underline"
          >
            Add work
          </button>
        </div>
        {directWork.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No goal-only work. Plan-linked work lives on each plan.
          </p>
        ) : (
          <ul className="space-y-2">
            {directWork.map((a) => (
              <EntityRow
                key={a.id}
                to={`/work/${a.id}`}
                title={a.title}
                meta={
                  <span>
                    {a.state}
                    {isOpenWork(a.state) ? " · open" : ""}
                  </span>
                }
              />
            ))}
          </ul>
        )}
      </section>

      <Modal open={planOpen} onClose={() => setPlanOpen(false)} labelledBy="add-plan">
        <form
          className="flex flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            if (!goalId || !planTitle.trim() || !planObjective.trim()) return;
            void createPlan
              .mutateAsync({
                title: planTitle.trim(),
                primaryObjective: planObjective.trim(),
                goalId,
              })
              .then(({ plan }) => {
                setPlanOpen(false);
                setPlanTitle("");
                setPlanObjective("");
                navigate(`/plans/${plan.id}`);
              });
          }}
        >
          <div className="border-b border-border px-4 py-3">
            <h2 id="add-plan" className="text-base font-semibold">
              Add plan
            </h2>
          </div>
          <div className="space-y-3 px-4 py-4">
            <input
              className={fieldClassName()}
              placeholder="Plan title"
              value={planTitle}
              onChange={(e) => setPlanTitle(e.target.value)}
              required
            />
            <textarea
              className={fieldClassName()}
              placeholder="Primary objective"
              rows={2}
              value={planObjective}
              onChange={(e) => setPlanObjective(e.target.value)}
              required
            />
          </div>
          <div className="flex justify-end gap-2 border-t border-border px-4 py-3">
            <button type="button" onClick={() => setPlanOpen(false)} className="text-sm text-muted-foreground">
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground"
            >
              Create
            </button>
          </div>
        </form>
      </Modal>

      <Modal open={workOpen} onClose={() => setWorkOpen(false)} labelledBy="add-work">
        <form
          className="flex flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            if (!goalId || !workTitle.trim()) return;
            void createActivity
              .mutateAsync({
                title: workTitle.trim(),
                type: "work",
                goalId,
              })
              .then(({ activity }) => {
                setWorkOpen(false);
                setWorkTitle("");
                navigate(`/work/${activity.id}`);
              });
          }}
        >
          <div className="border-b border-border px-4 py-3">
            <h2 id="add-work" className="text-base font-semibold">
              Add work
            </h2>
          </div>
          <div className="space-y-3 px-4 py-4">
            <input
              className={fieldClassName()}
              placeholder="Work title"
              value={workTitle}
              onChange={(e) => setWorkTitle(e.target.value)}
              required
            />
          </div>
          <div className="flex justify-end gap-2 border-t border-border px-4 py-3">
            <button type="button" onClick={() => setWorkOpen(false)} className="text-sm text-muted-foreground">
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground"
            >
              Create
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
